import { useCallback, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import type { LtiPlatform, LtiPlatformPage } from '../api/types';
import { useApi, useMutation } from '../lib/use-api';
import { formatDateTime, formatNumber } from '../lib/format';
import { EmptyNotice, ErrorNotice, Loading } from '../components/Feedback';
import { PlatformDialog } from '../components/PlatformDialog';
import { RegistrationTicketDialog } from '../components/RegistrationTicketDialog';

/** Vé đăng ký động do lti-tool cấp, chuyển tiếp qua delivery-api. */
interface RegistrationTicket {
  ticket: string;
  registerUrl: string;
  expiresAt: string;
}

/** Lớp bên LMS đã ánh xạ sang nhóm học viên. */
interface LtiContextGroup {
  platformId: string;
  contextId: string;
  groupPrincipalId: string;
  membershipsUrl?: string;
  lastSyncedAt?: string;
  lastMemberCount?: number;
}

interface ContextGroupPage {
  items: LtiContextGroup[];
}

/**
 * Quản lý nền tảng LTI — plan §5.1.
 *
 * Không có nút xóa, chỉ có vô hiệu hóa: `lti_launch` cascade từ `lti_platform`,
 * nên xóa một nền tảng là xóa sạch lịch sử lượt mở của nó.
 */
export function LtiPlatformsPage(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = i18n.resolvedLanguage ?? 'vi';

  const [editing, setEditing] = useState<LtiPlatform | 'new' | undefined>(undefined);
  const platforms = useApi<LtiPlatformPage>('/api/admin/lti/platforms');

  const disable = useMutation<LtiPlatform, unknown>(
    useCallback(
      (platform: LtiPlatform) =>
        apiFetch(`/api/admin/lti/platforms/${platform.id}/disable`, { method: 'POST', body: {} }),
      [],
    ),
  );

  const confirmDisable = async (platform: LtiPlatform): Promise<void> => {
    if (!window.confirm(t('lti.disableConfirm', { name: platform.displayName }))) return;
    const result = await disable.run(platform);
    if (result !== undefined) platforms.reload();
  };

  const rotate = useMutation<void, { kid: string }>(
    useCallback(
      () => apiFetch<{ kid: string }>('/api/admin/lti/keys/rotate', { method: 'POST', body: {} }),
      [],
    ),
  );

  /**
   * Rotate khoá ký. Hỏi xác nhận vì nó đổi khoá mà mọi LMS đang tin dùng —
   * platform phải tải lại JWKS mới verify được token phát sau đó. Khoá cũ
   * KHÔNG bị gỡ ngay (chuyển sang `retiring`), nên launch đang chạy không đứt.
   */
  const confirmRotate = async (): Promise<void> => {
    if (!window.confirm(t('lti.rotateConfirm'))) return;
    const result = await rotate.run();
    if (result !== undefined) platforms.reload();
  };

  /**
   * Sinh vé đăng ký động.
   *
   * Trước đây chỉ chạy được bằng `npm run lti:ticket`, nên bước đầu tiên của
   * việc nối LMS đòi quyền vào máy chủ.
   */
  const issueTicket = useMutation<void, RegistrationTicket>(
    useCallback(
      () =>
        apiFetch<RegistrationTicket>('/api/admin/lti/registration-tickets', {
          method: 'POST',
          body: {},
        }),
      [],
    ),
  );

  const [ticket, setTicket] = useState<RegistrationTicket | undefined>(undefined);

  const createTicket = async (): Promise<void> => {
    const issued = await issueTicket.run();
    if (issued) setTicket(issued);
  };

  const contextGroups = useApi<ContextGroupPage>('/api/admin/lti/context-groups');

  /**
   * Đồng bộ lại danh sách lớp từ LMS.
   *
   * Launch đã tự đồng bộ ở lần đầu của mỗi lớp, nhưng chỉ MỘT lần: học viên
   * ghi danh thêm sau đó chưa có trong nhóm cho tới khi bấm nút này.
   */
  const syncRoster = useMutation<LtiContextGroup, { total: number; removed: number }>(
    useCallback(
      (group: LtiContextGroup) =>
        apiFetch<{ total: number; removed: number }>(
          `/api/admin/lti/context-groups/${encodeURIComponent(group.contextId)}/sync`,
          { method: 'POST', body: { platformId: group.platformId } },
        ),
      [],
    ),
  );

  const runSync = async (group: LtiContextGroup): Promise<void> => {
    const result = await syncRoster.run(group);
    if (result !== undefined) contextGroups.reload();
  };

  return (
    <section>
      <h1>{t('lti.heading')}</h1>
      <p className="hint">{t('lti.intro')}</p>

      {platforms.data ? (
        <p className={platforms.data.keys.activeKid ? 'hint' : 'feedback feedback--error'}>
          {platforms.data.keys.activeKid
            ? t('lti.activeKey', { kid: platforms.data.keys.activeKid })
            : t('lti.noKey')}
        </p>
      ) : null}

      <div className="toolbar">
        <button type="button" onClick={() => setEditing('new')}>
          {t('lti.addPlatform')}
        </button>
        <button type="button" onClick={() => void createTicket()} disabled={issueTicket.pending}>
          {t('lti.createTicket')}
        </button>
        <button type="button" onClick={() => void confirmRotate()} disabled={rotate.pending}>
          {rotate.pending ? t('lti.rotating') : t('lti.rotateKey')}
        </button>
      </div>

      {platforms.loading ? <Loading /> : null}
      {platforms.error ? <ErrorNotice error={platforms.error} onRetry={platforms.reload} /> : null}
      {disable.error ? <ErrorNotice error={disable.error} /> : null}
      {rotate.error ? <ErrorNotice error={rotate.error} /> : null}
      {issueTicket.error ? <ErrorNotice error={issueTicket.error} /> : null}

      {platforms.data && !platforms.loading ? (
        platforms.data.items.length === 0 ? (
          <EmptyNotice />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('lti.column.displayName')}</th>
                <th scope="col">{t('lti.column.iss')}</th>
                <th scope="col">{t('lti.column.clientId')}</th>
                <th scope="col">{t('lti.column.deployments')}</th>
                <th scope="col">{t('lti.column.recentLaunches')}</th>
                <th scope="col">{t('lti.column.status')}</th>
                <th scope="col">{t('library.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {platforms.data.items.map((platform) => (
                <tr key={platform.id}>
                  <td>
                    {platform.displayName}
                    <small className="muted">
                      {' '}
                      {t(`lti.registeredBy.${platform.registeredBy}`)}
                    </small>
                  </td>
                  <td className="mono">{platform.iss}</td>
                  <td className="mono">{platform.clientId}</td>
                  <td className="mono">{platform.deploymentIds.join(', ') || '—'}</td>
                  <td>{formatNumber(platform.recentLaunches, locale)}</td>
                  <td>{t(`lti.status.${platform.status}`)}</td>
                  <td className="actions">
                    <button type="button" onClick={() => setEditing(platform)}>
                      {t('common:action.edit')}
                    </button>
                    {platform.status === 'active' ? (
                      <button type="button" onClick={() => void confirmDisable(platform)}>
                        {t('common:action.disable')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      <h2>{t('lti.roster.heading')}</h2>
      <p className="hint">{t('lti.roster.intro')}</p>

      {contextGroups.loading ? <Loading /> : null}
      {contextGroups.error ? (
        <ErrorNotice error={contextGroups.error} onRetry={contextGroups.reload} />
      ) : null}
      {syncRoster.error ? <ErrorNotice error={syncRoster.error} /> : null}

      {contextGroups.data && !contextGroups.loading ? (
        contextGroups.data.items.length === 0 ? (
          <EmptyNotice />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">{t('lti.roster.column.contextId')}</th>
                <th scope="col">{t('lti.roster.column.memberCount')}</th>
                <th scope="col">{t('lti.roster.column.lastSynced')}</th>
                <th scope="col">{t('library.column.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {contextGroups.data.items.map((group) => (
                <tr key={`${group.platformId}:${group.contextId}`}>
                  <td className="mono">{group.contextId}</td>
                  <td>
                    {group.lastMemberCount === undefined
                      ? '—'
                      : formatNumber(group.lastMemberCount, locale)}
                  </td>
                  <td>
                    {group.lastSyncedAt ? (
                      formatDateTime(group.lastSyncedAt, locale)
                    ) : (
                      <span className="muted">{t('lti.roster.neverSynced')}</span>
                    )}
                  </td>
                  <td className="actions">
                    <button
                      type="button"
                      onClick={() => void runSync(group)}
                      disabled={syncRoster.pending || !group.membershipsUrl}
                      title={group.membershipsUrl ? undefined : t('lti.roster.nrpsUnavailable')}
                    >
                      {syncRoster.pending ? t('lti.roster.syncing') : t('lti.roster.sync')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}

      {editing ? (
        <PlatformDialog
          platform={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            platforms.reload();
          }}
        />
      ) : null}

      {ticket ? (
        <RegistrationTicketDialog
          registerUrl={ticket.registerUrl}
          expiresAt={ticket.expiresAt}
          onClose={() => setTicket(undefined)}
        />
      ) : null}
    </section>
  );
}
